import { useModal } from '@sparklayer-io/spark-dash-ui/hooks/use-modal';
import { Alert, Button, Card, ExternalLink, Skeleton, Table } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { useHandlers } from '../../../context/page-context';
import type { PriceListApiResponse } from '../../price-lists/types';
import { DOCS_URLS, I18N_COMPONENTS } from '../constants';
import {
  canEditPriceList,
  isUnitOfMeasureAvailable,
  isUnitOfMeasureDisabled,
  isUnitOfMeasureRequired,
  validateEditedPrice,
  validateNewPrice,
} from '../editor-rules';
import { useUpdateVariantPricing } from '../hooks/use-update-variant-pricing';
import type { DraftRow, VariantPriceInput } from '../types';
import { PriceRow } from './price-row';
import { RemovePriceListModal } from './remove-price-list-modal';
import { RemovePriceModal } from './remove-price-modal';

type PriceListPricingCardProps = {
  sku: string;
  priceList: PriceListApiResponse;
  prices: VariantPriceInput[];
  draftRows: DraftRow[];
  isDraft: boolean;
  onAddDraftRow: () => void;
  onRemoveDraftRow: (index: number) => void;
  onDraftSaved: () => void;
  onRemoveDraftList: () => void;
};

export function PriceListPricingCard({
  sku,
  priceList,
  prices,
  draftRows,
  isDraft,
  onAddDraftRow,
  onRemoveDraftRow,
  onDraftSaved,
  onRemoveDraftList,
}: PriceListPricingCardProps) {
  const { notificationHandler } = useHandlers();
  const updatePricing = useUpdateVariantPricing(sku);
  const removePriceModal = useModal();
  const removeListModal = useModal();
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);

  const canEdit = canEditPriceList(priceList);
  const showUnitOfMeasure = isUnitOfMeasureAvailable(prices);
  const headings = [
    t(`${I18N_COMPONENTS}.price_list_pricing_card.headings.quantity`),
    ...(showUnitOfMeasure
      ? [t(`${I18N_COMPONENTS}.price_list_pricing_card.headings.unit_of_measure`)]
      : []),
    t(`${I18N_COMPONENTS}.price_list_pricing_card.headings.price`),
    ...(canEdit ? [t(`${I18N_COMPONENTS}.price_list_pricing_card.headings.actions`)] : []),
  ];

  const handleEditRow = (index: number, edited: VariantPriceInput) => {
    const error = validateEditedPrice(prices, index, edited);
    if (error) {
      notificationHandler.error(t(`${I18N_COMPONENTS}.price_list_pricing_card.errors.${error}`));
      return;
    }
    const next = [...prices];
    next[index] = edited;
    updatePricing.mutate([{ price_list_slug: priceList.slug, pricing: next }]);
  };

  const handleSaveDraftRow = (index: number, draft: VariantPriceInput) => {
    const error = validateNewPrice(prices, draft);
    if (error) {
      notificationHandler.error(t(`${I18N_COMPONENTS}.price_list_pricing_card.errors.${error}`));
      return;
    }
    updatePricing.mutate([{ price_list_slug: priceList.slug, pricing: [...prices, draft] }], {
      onSuccess: () => {
        onRemoveDraftRow(index);
        if (isDraft) {
          onDraftSaved();
        }
      },
    });
  };

  const handleRemoveRow = (index: number) => {
    setRemovingIndex(index);
    removePriceModal.openModal();
  };

  const confirmRemoveRow = () => {
    const next = prices.filter((_, index) => index !== removingIndex);
    updatePricing.mutate([{ price_list_slug: priceList.slug, pricing: next }], {
      onSettled: () => {
        removePriceModal.closeModal();
        setRemovingIndex(null);
      },
    });
  };

  const confirmRemoveList = () => {
    updatePricing.mutate([{ price_list_slug: priceList.slug, pricing: [] }], {
      onSettled: () => removeListModal.closeModal(),
    });
  };

  const handleRemoveList = () => {
    if (isDraft) {
      onRemoveDraftList();
    } else {
      removeListModal.openModal();
    }
  };

  return (
    <Card>
      <Card.Header
        headingLevel="h2"
        badges={[
          { text: priceList.slug, variant: 'default' },
          { text: priceList.currency_code.toUpperCase(), variant: 'green' },
        ]}
        primaryAction={
          canEdit
            ? {
                label: t('buttons:remove'),
                variant: 'dangerOutline',
                disabled: updatePricing.isPending,
                onClick: handleRemoveList,
              }
            : undefined
        }
      >
        {priceList.name}
      </Card.Header>
      <Card.Body>
        {!canEdit && (
          <Alert variant="info" headingLevel="h3">
            <span className="flex flex-wrap items-center gap-x-1">
              <span>{t(`${I18N_COMPONENTS}.price_list_pricing_card.not_editable`)}</span>
              <ExternalLink link="/pricing/lists" showIcon>
                {t(`${I18N_COMPONENTS}.price_list_pricing_card.manage_link`)}
              </ExternalLink>
            </span>
          </Alert>
        )}

        {updatePricing.isPending ? (
          <Skeleton />
        ) : (
          <Table headings={headings} caption={priceList.name}>
            {prices.map((price, index) => (
              <PriceRow
                key={`saved-${price.quantity}`}
                price={price}
                index={index}
                currencyCode={priceList.currency_code}
                canEdit={canEdit}
                showUnitOfMeasure={showUnitOfMeasure}
                isNew={false}
                isUnitOfMeasureRequired={isUnitOfMeasureRequired(prices, price, false)}
                isUnitOfMeasureDisabled={isUnitOfMeasureDisabled(prices, price)}
                onSave={(edited) => handleEditRow(index, edited)}
                onDelete={() => handleRemoveRow(index)}
              />
            ))}
            {draftRows.map((draft, index) => (
              <PriceRow
                key={draft.id}
                price={draft}
                index={index}
                currencyCode={priceList.currency_code}
                canEdit={canEdit}
                showUnitOfMeasure={showUnitOfMeasure}
                isNew
                isUnitOfMeasureRequired={isUnitOfMeasureRequired(prices, draft, true)}
                isUnitOfMeasureDisabled={isUnitOfMeasureDisabled(prices, draft)}
                onSave={(next) => handleSaveDraftRow(index, next)}
                onDelete={() => onRemoveDraftRow(index)}
              />
            ))}
          </Table>
        )}

        {canEdit && !updatePricing.isPending && (
          <div className="flex flex-wrap items-center gap-4">
            <Button variant="secondary" icon="plusCircle" onClick={onAddDraftRow}>
              {t(`${I18N_COMPONENTS}.price_list_pricing_card.add_price`)}
            </Button>
            {/* Card-level rather than per row: the docs cover the whole list, and inside the row it
                forced the unit-of-measure column wide enough for the input plus the link. */}
            {showUnitOfMeasure && (
              <ExternalLink link={DOCS_URLS.quantityPricing} showIcon>
                {t('links:learn_more')}
              </ExternalLink>
            )}
          </div>
        )}
      </Card.Body>

      <RemovePriceModal
        isOpen={removePriceModal.isOpen}
        onClose={removePriceModal.closeModal}
        onConfirm={confirmRemoveRow}
        loading={updatePricing.isPending}
      />
      <RemovePriceListModal
        slug={priceList.slug}
        isOpen={removeListModal.isOpen}
        onClose={removeListModal.closeModal}
        onConfirm={confirmRemoveList}
        loading={updatePricing.isPending}
      />
    </Card>
  );
}
